import React, { useState, useEffect } from "react";
import { TbLock, TbSparkles, TbCheck, TbHourglass } from "react-icons/tb";
import { Button, Modal } from "../components";
import { usePlan, GATE_COPY } from "../lib/plan";

// The upgrade surfaces for features a plan does not include.
//
// v1 met a free account with an upgrade modal on its dashboard; v2 dropped it and simply
// hid the paid areas, so nothing ever suggested there was more to buy. These put it back:
// a lock screen where a locked feature would be, a modal for a locked button, and a
// once-per-session welcome for Community accounts.

const PRO_POINTS = [
  "Lead magnets for your website, prospect scans and scan links",
  "Pipsqueak Snapshot internal scans",
  "AI-written reports and your own report templates",
  "Business reviews, assessments and review schedules",
];

/** What a gate is called and does. `gate` is a GATES key, or "PRO". */
const copyFor = (gate) => GATE_COPY[gate] || GATE_COPY.PRO;

/**
 * Shown in place of a screen the plan does not include. The admin may have switched a
 * module off for Pro too, in which case upgrading would not help — so the call to action
 * only appears for a tenant that is not already on Pro.
 */
export const LockedScreen = ({ gate }) => {
  const plan = usePlan();
  const { title, body } = copyFor(gate);
  return (
    <div className="v2-lock">
      <span className="v2-lock-icon"><TbLock size={22} /></span>
      <h2>{title} is not included in your plan</h2>
      <p>{body}</p>
      {plan.isPro ? (
        <p className="v2-lock-note">
          Your workspace is on Pro, but this feature has been switched off for it. Contact
          your Pipsqueak administrator to have it enabled.
        </p>
      ) : (
        <>
          <ul className="v2-lock-list">
            {PRO_POINTS.map((p) => <li key={p}><TbCheck size={14} /> {p}</li>)}
          </ul>
          <Button variant="primary" onClick={plan.upgrade}><TbSparkles size={15} /> Upgrade to Pro</Button>
        </>
      )}
    </div>
  );
};

/** The same message as a dialog, for a locked button inside a screen. */
export const UpgradeModal = ({ gate, onClose }) => {
  const plan = usePlan();
  const { title, body } = copyFor(gate);
  return (
    <Modal
      title={`${title} is a Pro feature`}
      onClose={onClose}
      footer={<>
        <Button onClick={onClose}>Not now</Button>
        {!plan.isPro && (
          <Button variant="primary" style={{ marginLeft: "auto" }} onClick={plan.upgrade}>
            <TbSparkles size={15} /> Upgrade to Pro
          </Button>
        )}
      </>}
    >
      <p className="v2-lock-modal-text">{body}</p>
      {plan.isPro && (
        <p className="v2-lock-modal-text">
          This feature has been switched off for your workspace. Contact your Pipsqueak
          administrator to have it enabled.
        </p>
      )}
    </Modal>
  );
};

// Once per browser session, not on every page load: v1's modal reopened on every visit
// to the dashboard, which teaches people to close it without reading it.
const WELCOME_KEY = "v2UpgradePromptSeen";

/** The Community welcome, and the thank-you after a checkout lands. */
export const PlanPrompts = () => {
  const plan = usePlan();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!plan.loaded || plan.isPro) return;
    let seen = false;
    try { seen = sessionStorage.getItem(WELCOME_KEY) === "1"; } catch { /* private mode */ }
    if (!seen) setOpen(true);
  }, [plan.loaded, plan.isPro]);

  const dismiss = () => {
    try { sessionStorage.setItem(WELCOME_KEY, "1"); } catch { /* private mode */ }
    setOpen(false);
  };

  if (plan.justUpgraded && plan.isPro) {
    return (
      <Modal title="Welcome to Pipsqueak Pro" onClose={plan.clearJustUpgraded}
             footer={<Button variant="primary" onClick={plan.clearJustUpgraded}>Get started</Button>}>
        <p className="v2-lock-modal-text">Your upgrade is complete. Everything below is now unlocked:</p>
        <ul className="v2-lock-list">
          {PRO_POINTS.map((p) => <li key={p}><TbCheck size={14} /> {p}</li>)}
        </ul>
      </Modal>
    );
  }

  if (!open) return null;
  return (
    <Modal
      // A workspace whose trial ran out is told that, not greeted as if it were new.
      title={plan.trialEnded ? "Your Pro trial has ended" : "You're on the free Community plan"}
      onClose={dismiss}
      footer={<>
        <Button onClick={dismiss}>Maybe later</Button>
        <Button variant="primary" style={{ marginLeft: "auto" }} onClick={() => { dismiss(); plan.upgrade(); }}>
          <TbSparkles size={15} /> Upgrade to Pro
        </Button>
      </>}
    >
      <p className="v2-lock-modal-text">
        {plan.trialEnded
          ? "Your workspace is now on the free Community plan. Upgrade to Pro to keep using:"
          : "Upgrade to Pro to unlock:"}
      </p>
      <ul className="v2-lock-list">
        {PRO_POINTS.map((p) => <li key={p}><TbCheck size={14} /> {p}</li>)}
      </ul>
      <p className="v2-lock-modal-text v2-lock-muted">
        Locked features stay in the menu with a <TbLock size={12} /> so you can see what each one does.
      </p>
    </Modal>
  );
};

/**
 * The days-left notice while a workspace is on its Pro trial. Quiet until the last three
 * days, then emphasised. Dismissible for the session, except on the final day.
 */
const TRIAL_BANNER_KEY = "v2TrialBannerHidden";
export const TrialBanner = () => {
  const plan = usePlan();
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem(TRIAL_BANNER_KEY) === "1"; } catch { return false; }
  });
  if (!plan.loaded || !plan.onTrial) return null;
  const days = plan.trialDaysLeft;
  const lastDay = days <= 1;
  if (hidden && !lastDay) return null;
  const urgent = days <= 3;
  const ends = plan.trialEndsAt
    ? new Date(plan.trialEndsAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;
  return (
    <div className={`v2-trial${urgent ? " urgent" : ""}`} role="status">
      <TbHourglass size={15} aria-hidden="true" />
      <span className="v2-trial-text">
        {lastDay ? <b>Your Pro trial ends today.</b> : <><b>{days} days left</b> in your Pro trial</>}
        {ends && !lastDay ? <span className="v2-trial-date"> · ends {ends}</span> : null}
      </span>
      <Button variant="primary" onClick={plan.upgrade}><TbSparkles size={14} /> Upgrade to Pro</Button>
      {!lastDay && (
        <button type="button" className="v2-trial-x" aria-label="Hide for now"
                onClick={() => { try { sessionStorage.setItem(TRIAL_BANNER_KEY, "1"); } catch { /* private mode */ } setHidden(true); }}>
          ×
        </button>
      )}
    </div>
  );
};
